/**
 * Branching-pipeline view derivation for the delivery dashboard.
 *
 * TWIN: the adjacency helpers, the dead-edge rule + cascade, and `layoutGraph`
 * are duplicated from forge/client/src/app/serviceManager/stageGraph.js
 * (authoring) and discovery/server/src/marketplace/common/stage-graph.helpers.ts
 * (the engine) — keep the copies in sync (no shared package exists).
 *
 * `layoutGraph` returns null for any flow that is not well-nested — the
 * round-trip check in `parseBlockTree` decides that, the same gate Forge
 * refuses structural edits with. A service authored outside Forge (scripts
 * write `next[]` directly, and the server accepts any valid DAG) therefore
 * draws no graph, and `StageFlowGraph` says so rather than rendering an empty
 * panel. A known, accepted gap; the constraint is deliberate.
 *
 * The dashboard renders a JOURNEY, not a graph: the trunk + taken path as
 * today's stepper, not-taken branches collapsed to ghost pills, and an
 * unresolved-but-reached fork as an explicit choice point. Customers (the
 * readOnly modal) get the journey and pills only — never choice buttons.
 */

export const usesExplicitEdges = (stages) =>
  (stages || []).some((s) => (s?.next || []).length > 0);

export function buildAdjacency(stages) {
  const adjacency = new Map();
  const explicit = usesExplicitEdges(stages);
  (stages || []).forEach((s, i) => {
    adjacency.set(
      s.stageKey,
      explicit
        ? [...(s.next || [])]
        : i + 1 < stages.length
        ? [stages[i + 1].stageKey]
        : []
    );
  });
  return adjacency;
}

export function buildPredecessors(stages) {
  const predecessors = new Map((stages || []).map((s) => [s.stageKey, []]));
  for (const [key, succs] of buildAdjacency(stages)) {
    for (const n of succs) {
      if (predecessors.has(n)) predecessors.get(n).push(key);
    }
  }
  return predecessors;
}

export function topologicalOrder(stages) {
  const adjacency = buildAdjacency(stages);
  const predecessors = buildPredecessors(stages);
  const indegree = new Map(
    (stages || []).map((s) => [
      s.stageKey,
      (predecessors.get(s.stageKey) || []).length,
    ])
  );
  const queue = (stages || [])
    .filter((s) => indegree.get(s.stageKey) === 0)
    .map((s) => s.stageKey);
  const order = [];
  while (queue.length) {
    const k = queue.shift();
    order.push(k);
    for (const n of adjacency.get(k) || []) {
      indegree.set(n, (indegree.get(n) || 0) - 1);
      if (indegree.get(n) === 0) queue.push(n);
    }
  }
  return order;
}

export function reachableDownstream(stages, fromKey) {
  const adjacency = buildAdjacency(stages);
  const seen = new Set();
  const queue = [...(adjacency.get(fromKey) || [])];
  while (queue.length) {
    const k = queue.shift();
    if (seen.has(k)) continue;
    seen.add(k);
    queue.push(...(adjacency.get(k) || []));
  }
  return seen;
}

const isClosed = (s) => s.status === "completed" || s.status === "skipped";

/**
 * Graph depth in COLUMNS (longest path, node count) — the horizontal/vertical
 * threshold input. A 9-stage two-branch graph has 5 columns and fits a
 * horizontal stepper better than a 9-stage linear pipeline.
 */
export function graphDepth(stages) {
  if (!stages?.length) return 0;
  const predecessors = buildPredecessors(stages);
  const depth = new Map();
  for (const k of topologicalOrder(stages)) {
    const preds = predecessors.get(k) || [];
    depth.set(
      k,
      preds.length ? Math.max(...preds.map((p) => depth.get(p) ?? 0)) + 1 : 0
    );
  }
  // `topologicalOrder` returns nothing when every stage sits in a cycle, and
  // `Math.max()` of an empty list is -Infinity — the one spot in this file that
  // would hand a layout nonsense instead of a safe fallback.
  return depth.size ? Math.max(...[...depth.values()]) + 1 : stages.length;
}

// ── TWIN — dead edges & the cascade (server stage-graph.helpers.ts) ─────────
//
// The server has THREE reasons an edge P → S is dead: the source was
// branch-skipped, the source is a fork resolved to a different successor, or
// the source is a fork that has ruled THIS arm out (`forkDeclinedArms`, design
// 2026-07-28 D1). Ported verbatim rather than approximated, so every surface
// that describes the graph asks the same predicate the engine writes by.

/**
 * Is the edge `parentKey → childKey` dead?
 *
 * Dead iff the source was BRANCH-skipped (the path not taken), or is a fork
 * resolved to a different successor, or is a fork that has ruled THIS arm out.
 * A MANUALLY skipped source keeps its outgoing edges live — the flow passes
 * through a waived stage.
 *
 * A resolution SUPERSEDES a decline of the arm it chose (server D9): the two
 * can race, and a fork pointing at an arm whose edge is dead would strand the
 * card.
 */
export function makeEdgeDeadPredicate(stages) {
  const adjacency = buildAdjacency(stages);
  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  const branchSkipped = new Set(
    (stages || [])
      .filter((s) => s.status === "skipped" && s.skippedVia === "branch")
      .map((s) => s.stageKey)
  );
  return (parentKey, childKey) => {
    if (branchSkipped.has(parentKey)) return true;
    if ((adjacency.get(parentKey) || []).length >= 2) {
      const parent = byKey.get(parentKey);
      if (parent?.forkResolvedTo) return parent.forkResolvedTo !== childKey;
      if ((parent?.forkDeclinedArms || []).includes(childKey)) return true;
    }
    return false;
  };
}

/** Everything still reachable from an entry stage over LIVE edges only. */
function liveReachable(stages) {
  const adjacency = buildAdjacency(stages);
  const predecessors = buildPredecessors(stages);
  const isEdgeDead = makeEdgeDeadPredicate(stages);
  const seen = new Set();
  const queue = (stages || [])
    .filter((s) => !(predecessors.get(s.stageKey) || []).length)
    .map((s) => s.stageKey);
  while (queue.length) {
    const k = queue.shift();
    if (seen.has(k)) continue;
    seen.add(k);
    for (const n of adjacency.get(k) || []) {
      if (!isEdgeDead(k, n)) queue.push(n);
    }
  }
  return seen;
}

/**
 * True when no live path still reaches `stageKey` — it sits on a path that was
 * resolved away. The server refuses to start OR complete such a stage, so no
 * surface here may offer it.
 */
export function isResolvedAway(stages, stageKey) {
  if (!usesExplicitEdges(stages)) return false;
  const preds = buildPredecessors(stages).get(stageKey) || [];
  if (!preds.length) return false; // an entry stage is never resolved away
  return !liveReachable(stages).has(stageKey);
}

/**
 * The cascade: every still-open stage whose inbound edges are ALL dead once
 * `changedStageKey` moves — the server's `cascadeFrom`, to the letter.
 * `resolvingParents` models forks being decided by this very click, whose CAS
 * has obviously not been persisted yet.
 */
function cascadeFrom(stages, changedStageKey, resolvingParents) {
  if (!usesExplicitEdges(stages)) return [];
  const adjacency = buildAdjacency(stages);
  const predecessors = buildPredecessors(stages);
  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  const branchSkippedNow = new Set(
    (stages || [])
      .filter((s) => s.status === "skipped" && s.skippedVia === "branch")
      .map((s) => s.stageKey)
  );
  const edgeIsDead = (p, s) => {
    if (branchSkippedNow.has(p)) return true;
    if ((adjacency.get(p) || []).length >= 2) {
      const resolvedTo = resolvingParents.get(p) ?? byKey.get(p)?.forkResolvedTo;
      if (resolvedTo) return resolvedTo !== s;
      if ((byKey.get(p)?.forkDeclinedArms || []).includes(s)) return true;
    }
    return false;
  };

  const toSkip = new Set();
  let changed = true;
  while (changed) {
    changed = false;
    for (const stage of stages || []) {
      const key = stage.stageKey;
      if (key === changedStageKey || toSkip.has(key)) continue;
      if (isClosed(stage)) continue;
      const preds = predecessors.get(key) || [];
      if (!preds.length) continue;
      if (preds.every((p) => edgeIsDead(p, key))) {
        toSkip.add(key);
        branchSkippedNow.add(key); // cascade additions ARE branch skips
        changed = true;
      }
    }
  }
  return [...toSkip];
}

/**
 * Fork parents of `stageKey` whose choice is still open — the client twin of
 * the server's `unresolvedForkParents`. This is the DIRECT-parent question,
 * and it is the one the server's decline path asks (`fulfillment.service.ts`,
 * "Declining an arm"): skipping an arm ENTRANCE declines that arm, skipping a
 * stage in an arm's interior is an ordinary manual waiver.
 */
export function unresolvedForkParents(stages, stageKey) {
  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  return (buildPredecessors(stages).get(stageKey) || []).filter((p) => {
    const parent = byKey.get(p);
    return (parent?.next || []).length >= 2 && !parent?.forkResolvedTo;
  });
}

/**
 * TWIN of the server's `computeBranchEffects` (stage-graph.helpers.ts) — design
 * 2026-07-28 D1. Which forks starting `stageKey` decides, and which arms it
 * merely rules out, by REACHABILITY (a stage deep inside an arm counts):
 *
 *   live = the arms of a fork from which `stageKey` is reachable
 *   |live| == 1 → RESOLVE that fork to that arm   (siblings die)
 *   |live| >= 2 → DECLINE the arms that cannot lead here; the fork stays open
 *
 * The cascade is mirrored (`skips`) rather than re-derived from adjacency: a
 * confirmation is a promise about consequences, so it must compute exactly the
 * skip set the server will.
 */
export function forkEffectsFor(stages, stageKey) {
  const resolutions = [];
  const declines = [];
  if (!usesExplicitEdges(stages)) return { resolutions, declines, skips: [] };
  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));

  for (const [forkKey, arms] of buildAdjacency(stages)) {
    if ((arms || []).length < 2) continue;
    const fork = byKey.get(forkKey);
    if (fork?.forkResolvedTo) continue; // already decided

    const declined = fork?.forkDeclinedArms || [];
    const live = arms.filter((arm) => {
      // An arm already ruled out is not a candidate to resolve TO. It can
      // still be reachable — a declined arm entrance may double as a
      // convergence on a sibling arm — and counting it would let a later
      // start resolve the fork to an arm whose edge was declared dead,
      // reviving it (audit F14).
      if (declined.includes(arm)) return false;
      const reach = reachableDownstream(stages, arm);
      reach.add(arm);
      return reach.has(stageKey);
    });
    if (!live.length) continue; // this fork is not above us

    if (live.length === 1) {
      resolutions.push({ forkKey, armKey: live[0] });
      continue;
    }
    for (const arm of arms) {
      if (live.includes(arm)) continue;
      if (declined.includes(arm)) continue;
      declines.push({ forkKey, armKey: arm });
    }
  }

  // Model both halves as if already persisted, then run the one cascade —
  // exactly the server's order.
  const declinedByFork = new Map();
  for (const { forkKey, armKey } of declines) {
    declinedByFork.set(forkKey, [...(declinedByFork.get(forkKey) || []), armKey]);
  }
  const projected = (stages || []).map((s) => {
    const declined = declinedByFork.get(s.stageKey);
    return declined
      ? { ...s, forkDeclinedArms: [...(s.forkDeclinedArms || []), ...declined] }
      : s;
  });
  const resolvingParents = new Map(
    resolutions.map(({ forkKey, armKey }) => [forkKey, armKey])
  );

  return {
    resolutions,
    declines,
    skips: cascadeFrom(projected, stageKey, resolvingParents),
  };
}

/**
 * True when starting `stageKey` would DECIDE a fork — by reachability, so a
 * stage deep inside an arm counts.
 *
 * This is not the question the Skip button asks; see `declinesPathBySkip`. No
 * surface builds copy from this (`startConsequence` owns that), but it names
 * the distinction the two questions turn on, and the tests contrast them here.
 */
export function resolvesFork(stages, stageKey) {
  if (!usesExplicitEdges(stages)) return false;
  return forkEffectsFor(stages, stageKey).resolutions.length > 0;
}

/**
 * Every stage starting `stageKey` would close for good — the server's own
 * projected skip set, in the server's own topological order. Naming the
 * cascade's own output is the only version that cannot drift from the engine.
 */
export function stagesClosedByStart(stages, stageKey) {
  const order = new Map(topologicalOrder(stages).map((k, i) => [k, i]));
  return forkEffectsFor(stages, stageKey).skips.sort(
    (a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0)
  );
}

/** Human list of stage names, capped so a confirm stays readable. */
function nameList(stages, keys, cap = 3) {
  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  const names = keys.map((k) => `“${byKey.get(k)?.stageName || k}”`);
  if (names.length <= cap) return names.join(", ");
  return `${names.slice(0, cap).join(", ")} and ${names.length - cap} more`;
}

/**
 * What starting `stageKey` will do, as the sentence to confirm it with — or
 * `{ confirms: false }` when it is an ordinary one-click start.
 *
 * THE ONE IMPLEMENTATION: a consequence belongs to the CARD, so it is derived
 * once, here, and every button that can start a stage renders it — rather than
 * each surface hand-rolling its own warning and drifting out of agreement.
 */
export function startConsequence(stages, stageKey) {
  if (!usesExplicitEdges(stages)) return { confirms: false };
  const { resolutions, skips } = forkEffectsFor(stages, stageKey);

  // No skips ⇒ nothing is lost ⇒ nothing to confirm. An empty skip set proves
  // every stage is still reachable — the fork resolution only killed edges that
  // could not have been travelled anyway (the diamond case). A confirmation
  // that would read "nothing happens" is noise that teaches ops to click
  // through the confirmations that matter.
  if (!skips.length) return { confirms: false };

  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  const name = byKey.get(stageKey)?.stageName || stageKey;
  const closes = nameList(stages, stagesClosedByStart(stages, stageKey));

  // A resolution DECIDES the fork; a decline only rules arms out. Telling ops
  // "this picks a path" while the choice is still open would be a lie.
  return resolutions.length
    ? {
        confirms: true,
        confirmLabel: "Take this path",
        message: `Starting “${name}” picks this path: ${closes} will be skipped. This can't be undone.`,
      }
    : {
        confirms: true,
        confirmLabel: "Start anyway",
        message: `Starting “${name}” rules out the paths that can't lead here — the choice itself stays open. ${closes} will be skipped. This can't be undone.`,
      };
}

/**
 * True when SKIPPING `stageKey` declines a path rather than waiving a stage.
 *
 * The server's decline is deliberately ENTRANCE-ONLY: it asks
 * `unresolvedForkParents` — direct fork parents — so skipping a stage in an
 * arm's INTERIOR is an ordinary manual waiver that marks one stage, cascades
 * nothing and leaves every edge live. (Skip's semantics stayed entrance-only;
 * only Start's moved to reachability.)
 */
export function declinesPathBySkip(stages, stageKey) {
  if (!usesExplicitEdges(stages)) return false;
  const stage = (stages || []).find((s) => s.stageKey === stageKey);
  if (stage?.status !== "pending") return false; // only a pending arm declines
  return unresolvedForkParents(stages, stageKey).length > 0;
}

/** What ELSE dies when `stageKey` is declined — the server's decline cascade. */
export function stagesClosedByDecline(stages, stageKey) {
  if (!declinesPathBySkip(stages, stageKey)) return [];
  const projected = (stages || []).map((s) =>
    s.stageKey === stageKey
      ? { ...s, status: "skipped", skippedVia: "branch" }
      : s
  );
  const order = new Map(topologicalOrder(stages).map((k, i) => [k, i]));
  return cascadeFrom(projected, stageKey, new Map()).sort(
    (a, b) => (order.get(a) ?? 0) - (order.get(b) ?? 0)
  );
}

/**
 * Direct predecessors of `stageKey` that are still open — i.e. the stages this
 * one was started ahead of. Informational only: stages are not sequential and
 * an out-of-order start is never refused (design 2026-07-28 D8).
 */
export function openPredecessors(stages, stageKey) {
  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  return (buildPredecessors(stages).get(stageKey) || []).filter((k) => {
    const s = byKey.get(k);
    return s && (s.status === "pending" || s.status === "in_progress");
  });
}

/**
 * The one sentence explaining that a stage is out of step with its neighbours,
 * or null when it is not — the twin of `skipExplanation`, and informational in
 * exactly the same way. Stages are NOT sequential: `next[]` declares layout and
 * mutual exclusivity, never a precondition, so nothing here gates an action.
 * It exists because a stage jumped over otherwise looks identical to one whose
 * turn simply has not come (design 2026-07-28 D8).
 */
export function outOfOrderNote(stages, stageKey) {
  if (!usesExplicitEdges(stages)) return null;
  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  const stage = byKey.get(stageKey);
  if (!stage) return null;

  if (stage.status !== "pending") {
    const ahead = openPredecessors(stages, stageKey).map(
      (k) => byKey.get(k)?.stageName || k
    );
    if (ahead.length) {
      return `Started ahead of ${ahead.map((n) => `"${n}"`).join(" and ")}`;
    }
    return null;
  }
  return isJumpedOver(stages, stageKey)
    ? "Jumped over — the flow moved on without it"
    : null;
}

/**
 * True when `stageKey` is still pending but the flow has already moved past it
 * — the counterpart cue to `openPredecessors`, rendered on the stage that was
 * skipped over. A branch-skipped descendant does not count: that is the path
 * not taken, not evidence anybody jumped this stage.
 */
export function isJumpedOver(stages, stageKey) {
  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  if (byKey.get(stageKey)?.status !== "pending") return false;
  return [...reachableDownstream(stages, stageKey)].some((k) => {
    const s = byKey.get(k);
    if (!s || s.status === "pending") return false;
    return !(s.status === "skipped" && s.skippedVia === "branch");
  });
}

/**
 * The arms of `forkKey` that are genuinely still on offer: the fork's edge to
 * them is live (not declined, not resolved away from) and the stage itself is
 * still pending. The one place "what can ops choose here" is decided.
 */
function liveForkOptions(stages, fork, byKey, isEdgeDead) {
  return (fork.next || [])
    .filter((k) => !isEdgeDead(fork.stageKey, k))
    .map((k) => byKey.get(k))
    .filter((succ) => succ && succ.status === "pending");
}

/**
 * The card's pending path choice, or null — `{ parentKey, options }`.
 *
 * ONE predicate for every surface. ≥2 live arms is the honest threshold: one
 * remaining arm is not a choice, it is the next step. Declined arms are
 * excluded — their fork edge is dead, so offering them would offer a path the
 * server refuses to start.
 */
export function unresolvedForkFrontier(stages) {
  if (!usesExplicitEdges(stages)) return null;
  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  const isEdgeDead = makeEdgeDeadPredicate(stages);
  for (const s of stages || []) {
    if ((s.next || []).length < 2 || !isClosed(s) || s.forkResolvedTo) continue;
    const options = liveForkOptions(stages, s, byKey, isEdgeDead);
    if (options.length >= 2) return { parentKey: s.stageKey, options };
  }
  return null;
}

/**
 * True when the card is parked at an unresolved fork. Board-card cue
 * ("⑂ Choose path") — the same question the modal's choice node answers.
 */
export function hasUnresolvedForkFrontier(stages) {
  return unresolvedForkFrontier(stages) !== null;
}

/**
 * Derive the branched-stepper view. Returns null for a linear card (render
 * exactly as today). Otherwise:
 *   journey  — topo-ordered stages minus branch-skipped ones (manual/terminal
 *              skips STAY — they're on the taken path, rendered gray);
 *   pendingFork — { parentKey, options: stage[] } when the frontier is an
 *              unresolved fork (options = its still-pending successors);
 *   depth    — graph columns (longest path) for the layout threshold.
 */
export function deriveBranchView(stages) {
  if (!usesExplicitEdges(stages)) return null;

  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  const topo = topologicalOrder(stages);
  const branchSkipped = (s) =>
    s.status === "skipped" && s.skippedVia === "branch";

  const journey = topo
    .map((k) => byKey.get(k))
    .filter((s) => s && !branchSkipped(s));

  // Unresolved fork frontier → choice node. The frontier is specifically a
  // fork whose parent has CLOSED: that is when the choice becomes actionable.
  // Same predicate the board's chip uses — see `unresolvedForkFrontier`.
  const pendingFork = unresolvedForkFrontier(stages);

  // The journey stops at ANY undecided fork, not only at an actionable one:
  // two live options are a question, never a plan, so neither may render as an
  // upcoming step even while the fork's parent is still in progress.
  const isEdgeDead = makeEdgeDeadPredicate(stages);
  const undecided = new Set();
  for (const s of stages || []) {
    if ((s.next || []).length < 2 || s.forkResolvedTo) continue;
    const open = liveForkOptions(stages, s, byKey, isEdgeDead);
    if (open.length < 2) continue; // narrowed to one — that IS the next step
    open.forEach((o) => {
      undecided.add(o.stageKey);
      reachableDownstream(stages, o.stageKey).forEach((k) => undecided.add(k));
    });
  }
  // Hide an undecided FUTURE, never work that happened.
  //
  // "Everything reachable from an undecided arm" is a set over the GRAPH, and on
  // a cross-linked flow it sweeps up already-completed stages — including the
  // one the card is sitting on. So the filter is restricted to `pending`: not
  // showing an unmade choice is information design, but hiding work that was
  // performed is a false report. An in-progress or closed stage is evidence,
  // and evidence is never hidden.
  const journeyStages = undecided.size
    ? journey.filter(
        (s) => !(undecided.has(s.stageKey) && s.status === "pending")
      )
    : journey;

  return {
    journey: journeyStages,
    pendingFork,
    depth: graphDepth(stages),
  };
}

/**
 * One summary per RESOLVED fork, for the "Path A taken · Path B skipped" chip.
 * Letters follow the fork parent's next[] order — the same order Forge
 * serializes paths in, so letters match the authoring UI.
 *
 * WHICH ARMS DIED IS A FACT ABOUT THE CARD, NOT ARITHMETIC ON `next[]`: each
 * non-taken arm is classified by what actually became of it, because on a
 * diamond an arm can survive on a live edge from elsewhere (`forkDeclinedArms`)
 * and the server skips nothing.
 *
 * And a resolution is only an OUTCOME once the fork's own stage has run.
 * `computeBranchEffects` resolves fork ANCESTORS by reachability regardless of
 * whether that fork happened; that record is load-bearing for edge-death and
 * stays, but must not render as a decision somebody took. A fork that has not
 * run, and whose arms are all still alive, has nothing to report and is
 * omitted.
 */
export function resolvedForkSummaries(stages) {
  const byKey = new Map((stages || []).map((s) => [s.stageKey, s]));
  const out = [];
  for (const s of stages || []) {
    if (!((s.next || []).length >= 2 && s.forkResolvedTo)) continue;
    if (!byKey.has(s.forkResolvedTo)) continue;
    const entry = (k, i) => ({
      stageKey: k,
      letter: String.fromCharCode(65 + i),
      stageName: byKey.get(k)?.stageName || k,
    });
    const taken = s.next
      .map((k, i) => (k === s.forkResolvedTo ? entry(k, i) : null))
      .find(Boolean);
    const others = s.next
      .map((k, i) => (k !== s.forkResolvedTo ? entry(k, i) : null))
      .filter(Boolean);
    const isBranchSkipped = (k) => {
      const st = byKey.get(k);
      return st?.status === "skipped" && st?.skippedVia === "branch";
    };
    const skipped = others.filter((o) => isBranchSkipped(o.stageKey));
    const stillReachable = others.filter((o) => !isBranchSkipped(o.stageKey));
    // Nothing closed and the fork itself never ran — an inference, not an
    // outcome. Saying anything here would be inventing a decision.
    if (!skipped.length && !isClosed(s)) continue;
    out.push({ forkKey: s.stageKey, taken, skipped, stillReachable });
  }
  return out;
}

/**
 * The JOURNEY is what actually happened on this card. A branch-skipped stage
 * is the path NOT taken — not work that was waived, so it is neither progress
 * nor a denominator. Every count in the dashboard (detail header, board
 * fraction, stage dots) runs through this one filter, so they cannot disagree.
 */
export function journeyStages(stages) {
  return (stages || []).filter(
    (s) => !(s.status === "skipped" && s.skippedVia === "branch")
  );
}

/**
 * True when closing `stageKey` leaves no other open stage on the journey —
 * i.e. the action runs allCompleted → completeFulfillment → consumeService
 * and spends the customer's credit. Skipping
 * the last open stage does this exactly like completing it, which is why the
 * Skip button needs a confirmation.
 */
export function wouldCloseCard(stages, stageKey) {
  const journey = journeyStages(stages);
  if (!journey.some((s) => s.stageKey === stageKey)) return false;
  return journey.every(
    (s) =>
      s.stageKey === stageKey ||
      s.status === "completed" ||
      s.status === "skipped"
  );
}

/**
 * How many still-open stages a terminal completion would close with it.
 *
 * Every card, both linear and branched: completing an `isTerminal` stage
 * short-circuits the pipeline, skips whatever is still open and consumes the
 * credit (fulfillment.service.ts, "Terminal short-circuit"). The mirror
 * follows the server so a terminal Complete always warns before it spends.
 */
export function stagesClosedByTerminal(stages, stageKey) {
  const stage = (stages || []).find((s) => s.stageKey === stageKey);
  if (!stage?.isTerminal) return 0;
  return journeyStages(stages).filter(
    (s) =>
      s.stageKey !== stageKey &&
      (s.status === "pending" || s.status === "in_progress")
  ).length;
}

/**
 * The one sentence explaining why a stage is skipped, or null if it isn't.
 *
 * System skips derive their wording from `skippedVia` right here — it is never
 * stored. That keeps the copy editable without a migration, and keeps
 * `skipReason` meaning exactly one thing: a person typed that. A stage closed
 * by a cascade has no reason to show because nobody had one to give.
 *
 * Ops-facing only — the customer journey never renders this.
 */
export function skipExplanation(stage) {
  if (!stage || stage.status !== "skipped") return null;
  switch (stage.skippedVia) {
    case "terminal":
      return "Card completed before this stage could run";
    case "branch":
      return "Path not taken";
    case "manual":
    default:
      if (stage.skipReason) return `Skipped — ${stage.skipReason}`;
      return stage.updatedByName
        ? `Skipped by ${stage.updatedByName}`
        : "Skipped";
  }
}

/**
 * Arm letters for every fork successor: successor i of a 2+-way fork is arm
 * A, B, … — the SAME index `resolvedForkSummaries` letters by, so the
 * "Path A taken" chip can finally be mapped onto the picture. Mirrors Forge's
 * `pathLetterOf`.
 */
export function forkArmLetters(stages) {
  const out = new Map();
  for (const s of stages || []) {
    const next = s.next || [];
    if (next.length < 2) continue;
    next.forEach((k, i) => {
      if (!out.has(k)) out.set(k, String.fromCharCode(65 + (i % 26)));
    });
  }
  return out;
}

/** Keep a docked inspector's caret inside its panel (px from the left edge). */
export function clampCaret(x, panelWidth, margin = 16) {
  if (!panelWidth) return margin;
  return Math.min(Math.max(x, margin), Math.max(margin, panelWidth - margin));
}

// ── TWIN section — ported from forge/client stageGraph.js (keep in sync) ──

/** Structural validity only (dangling/dupe/cycle/entry/orphan) — parse gate. */
export function structuralGraphErrors(stages) {
  const errors = [];
  if (!Array.isArray(stages) || stages.length === 0) return errors;
  if (!usesExplicitEdges(stages)) return errors;
  const keySet = new Set(stages.map((s) => s.stageKey));
  for (const s of stages) {
    for (const n of s.next || []) {
      if (!keySet.has(n)) errors.push(`unknown successor ${n}`);
      if (n === s.stageKey) errors.push(`self edge ${n}`);
    }
    if ((s.next || []).some((n, i, a) => a.indexOf(n) !== i))
      errors.push(`duplicate successors on ${s.stageKey}`);
  }
  if (errors.length) return errors;
  if (topologicalOrder(stages).length !== stages.length) {
    errors.push("cycle");
    return errors;
  }
  const predecessors = buildPredecessors(stages);
  const entries = stages.filter(
    (s) => (predecessors.get(s.stageKey) || []).length === 0
  );
  if (entries.length !== 1) {
    errors.push("entries != 1");
    return errors;
  }
  const reachable = reachableDownstream(stages, entries[0].stageKey);
  reachable.add(entries[0].stageKey);
  if (stages.some((s) => !reachable.has(s.stageKey))) errors.push("orphans");
  return errors;
}

/**
 * Serialize a block tree back to a flat stage array: depth-first in sequence
 * order (fork parent → each path's stages → the stage after the block), with
 * explicit `next` on every stage. `exit` is where a sequence flows when it runs
 * off its end (null = dead end).
 *
 * TWIN of Forge's `serializeBlockTree`. It exists here for ONE purpose — the
 * round-trip check below. Nothing in the dashboard authors a graph.
 */
function serializeBlockTree(seq, exit = null) {
  const out = [];

  const entryOf = (items, pathExit) => {
    if (!items.length) return pathExit; // empty path = direct edge to its exit
    const first = items[0];
    if (first.kind !== "stage")
      throw new Error("A path/sequence must start with a stage");
    return first.stage.stageKey;
  };

  const emitSeq = (items, seqExit) => {
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.kind === "stage") {
        const following = items[i + 1];
        let nextKeys;
        if (following?.kind === "stage") {
          nextKeys = [following.stage.stageKey];
        } else if (following?.kind === "branch") {
          const afterBranch = items[i + 2];
          const branchBelow =
            afterBranch?.kind === "stage" ? afterBranch.stage.stageKey : seqExit;
          nextKeys = [
            ...new Set(
              following.paths
                .map((p) => {
                  const tail = p.tail || { kind: "below" };
                  const pathExit =
                    tail.kind === "at"
                      ? tail.target
                      : tail.kind === "end"
                      ? null
                      : branchBelow;
                  return entryOf(p.items, pathExit);
                })
                .filter(Boolean)
            ),
          ];
        } else {
          nextKeys = seqExit ? [seqExit] : [];
        }
        out.push({ ...item.stage, next: nextKeys });
      } else {
        const after = items[i + 1];
        const belowKey =
          after?.kind === "stage" ? after.stage.stageKey : seqExit;
        for (const path of item.paths) {
          const tail = path.tail || { kind: "below" };
          const pathExit =
            tail.kind === "at"
              ? tail.target
              : tail.kind === "end"
              ? null
              : belowKey;
          emitSeq(path.items, pathExit);
        }
      }
    }
  };

  emitSeq(seq, exit);
  return out;
}

function parseBlockTree(stages) {
  if (!Array.isArray(stages) || stages.length === 0) return { seq: [] };
  if (!usesExplicitEdges(stages)) {
    return { seq: stages.map((s) => ({ kind: "stage", stage: s })) };
  }
  if (structuralGraphErrors(stages).length) return null;

  const adjacency = buildAdjacency(stages);
  const byKey = new Map(stages.map((s) => [s.stageKey, s]));
  const topo = topologicalOrder(stages);
  const topoIndex = new Map(topo.map((k, i) => [k, i]));
  const predecessors = buildPredecessors(stages);
  const entry = stages.find(
    (s) => (predecessors.get(s.stageKey) || []).length === 0
  ).stageKey;

  const joinOf = (forkKey) => {
    let intersection = null;
    for (const s of adjacency.get(forkKey) || []) {
      const r = reachableDownstream(stages, s);
      r.add(s);
      intersection = intersection
        ? new Set([...intersection].filter((k) => r.has(k)))
        : r;
    }
    if (!intersection || intersection.size === 0) return null;
    return [...intersection].sort(
      (a, b) => topoIndex.get(a) - topoIndex.get(b)
    )[0];
  };

  const walkSeq = (startKey, stopKey) => {
    const items = [];
    let cur = startKey;
    const stopIdx = stopKey != null ? topoIndex.get(stopKey) : Infinity;
    for (;;) {
      if (cur == null) return { items, tail: { kind: "end" } };
      if (cur === stopKey) return { items, tail: { kind: "below" } };
      if (topoIndex.get(cur) > stopIdx) {
        return { items, tail: { kind: "at", target: cur } };
      }
      items.push({ kind: "stage", stage: byKey.get(cur) });
      const succs = adjacency.get(cur) || [];
      if (succs.length === 0) return { items, tail: { kind: "end" } };
      if (succs.length === 1) {
        cur = succs[0];
        continue;
      }
      const join = joinOf(cur);
      // A fork whose arms don't all meet again inside THIS sequence: either
      // nothing post-dominates it (an arm finishes the delivery), or the stage
      // they do meet at lies past our own stop (an arm jumps over the enclosing
      // join — a cross-merge, the "early exit" shape). A path already carries
      // `tail: {kind:"at", target}` ("leaves here, continues at X"), so walking
      // the arms against OUR stop lets each one say where it goes and the block
      // closes where it opened instead of staying open past its parent.
      //
      // Correctness rests on the round-trip check at the bottom of this
      // function: anything the tree can't hold faithfully comes back null and
      // the op is refused.
      const joinPastStop =
        join != null && stopKey != null && topoIndex.get(join) > stopIdx;
      if (join == null || joinPastStop) {
        // TWIN of Forge stageGraph.js — keep the two in step.
        //
        // No stage post-dominates this fork: one arm FINISHES the delivery and
        // a sibling carries on (the alternative-endings shape). Each arm stops
        // where THIS sequence stops, but only if it can reach it — a dead-ending
        // arm reaches nothing, and topological order may place its leaves after
        // the join, which would make the `topoIndex > stopIdx` test above fire
        // immediately and drop the leaf from the tree.
        const armStop = (s) => {
          if (stopKey == null) return null;
          const r = reachableDownstream(stages, s);
          r.add(s);
          // Reaches our stop → stop there. Overshoots it → stop there too, so
          // the walk hands back `tail: {at: …}` for the stage it jumps to.
          // Reaches neither → it dead-ends, and `topologicalOrder` may place
          // its leaves after the stop, which would make the "beyond our stop"
          // test above fire on the arm's own first stage and lose it.
          return r.has(stopKey) || joinPastStop ? stopKey : null;
        };
        const armPaths = succs.map((s) => walkSeq(s, armStop(s)));
        items.push({ kind: "branch", paths: armPaths });
        const flowsBelow =
          stopKey != null && armPaths.some((p) => p.tail.kind === "below");
        return { items, tail: { kind: flowsBelow ? "below" : "end" } };
      }
      const paths = succs.map((s) => walkSeq(s, join));
      items.push({ kind: "branch", paths });
      cur = join === stopKey ? stopKey : join;
      if (cur === stopKey) return { items, tail: { kind: "below" } };
    }
  };

  const root = walkSeq(entry, null);
  const seq = root.items;

  // THE ROUND-TRIP CHECK IS THE GATE. `walkSeq` is written ASSUMING it is
  // gated: every hard case in it (a fork with no join, an arm that jumps past
  // the enclosing join) is handled optimistically because anything it gets
  // wrong is supposed to come back null here. An unfaithful diagram of a
  // delivery is worse than no diagram, which is why `StageFlowGraph` has an
  // explicit empty state for null.
  //
  // Integrity = same stage set, same edge set.
  let flat;
  try {
    flat = serializeBlockTree(seq, null);
  } catch (e) {
    return null;
  }
  if (flat.length !== stages.length) return null;
  const edgeSet = (arr) =>
    new Set(arr.flatMap((s) => (s.next || []).map((n) => `${s.stageKey}→${n}`)));
  const a = edgeSet(stages);
  const b = edgeSet(flat);
  if (a.size !== b.size || [...a].some((e) => !b.has(e))) return null;

  return { seq };
}

/**
 * Deterministic layered layout (col = longest-path depth; lanes from the
 * block tree). Returns { nodes:[{key,col,lane}], edges, cols, lanes } or
 * null when the graph isn't well-nested.
 */
export function layoutGraph(stages) {
  if (!Array.isArray(stages) || stages.length === 0)
    return { nodes: [], edges: [], cols: 0, lanes: 0 };
  const tree = parseBlockTree(stages);
  if (!tree) return null;

  const adjacency = buildAdjacency(stages);
  const predecessors = buildPredecessors(stages);
  const depth = new Map();
  for (const k of topologicalOrder(stages)) {
    const preds = predecessors.get(k) || [];
    depth.set(
      k,
      preds.length ? Math.max(...preds.map((p) => depth.get(p) ?? 0)) + 1 : 0
    );
  }

  const lanesNeeded = (items) =>
    Math.max(
      1,
      ...items.map((it) =>
        it.kind === "stage"
          ? 1
          : it.paths.reduce((sum, p) => sum + lanesNeeded(p.items), 0)
      )
    );
  const lane = new Map();
  const assign = (items, laneStart, laneCount) => {
    const center = laneStart + (laneCount - 1) / 2;
    for (const it of items) {
      if (it.kind === "stage") {
        lane.set(it.stage.stageKey, center);
      } else {
        let cursor = laneStart;
        for (const p of it.paths) {
          const need = lanesNeeded(p.items);
          assign(p.items, cursor, need);
          cursor += need;
        }
      }
    }
  };
  const totalLanes = lanesNeeded(tree.seq);
  assign(tree.seq, 0, totalLanes);

  const nodes = stages.map((s) => ({
    key: s.stageKey,
    col: depth.get(s.stageKey) ?? 0,
    lane: lane.get(s.stageKey) ?? 0,
  }));
  const edges = stages.flatMap((s) =>
    (adjacency.get(s.stageKey) || []).map((n) => ({ from: s.stageKey, to: n }))
  );
  return {
    nodes,
    edges,
    cols: Math.max(...nodes.map((n) => n.col)) + 1,
    lanes: totalLanes,
  };
}

/**
 * Step/column numbering (drawing semantics): leading integer = the step
 * (longest-path depth + 1 — equals the rendered column; parallel stages share
 * it), dotted suffix = the branch lane (3.1, then 4.2.1 inside branch 2's own
 * fork); suffix drops at a convergence back to the enclosing sequence; a
 * shorter path SKIPS numbers into a convergence (4.2.3 -> 6, no 5.x). Mirror
 * of the server's computeStageNumbers — the saved value stays authoritative.
 */
export function computeStageNumbers(stages) {
  const out = new Map();
  if (!Array.isArray(stages) || stages.length === 0) return out;
  if (!usesExplicitEdges(stages) || structuralGraphErrors(stages).length) {
    // Linear (or mid-edit broken) → positional; never blank, never a crash.
    stages.forEach((s, i) => out.set(s.stageKey, String(i + 1)));
    return out;
  }

  const adjacency = buildAdjacency(stages);
  const predecessors = buildPredecessors(stages);
  const topo = topologicalOrder(stages);
  const topoIndex = new Map(topo.map((k, i) => [k, i]));
  const entry = stages.find(
    (s) => (predecessors.get(s.stageKey) || []).length === 0
  );

  // Column = longest-path depth from the entry. The LEADING number is the
  // column (parallel stages share it; short paths skip numbers into a
  // convergence); the dotted suffix is the branch lane.
  const depth = new Map();
  for (const k of topo) {
    const preds = predecessors.get(k) || [];
    depth.set(
      k,
      preds.length ? Math.max(...preds.map((p) => depth.get(p) ?? 0)) + 1 : 0
    );
  }

  const joinOf = (forkKey) => {
    let intersection = null;
    for (const s of adjacency.get(forkKey) || []) {
      const r = reachableDownstream(stages, s);
      r.add(s);
      intersection = intersection
        ? new Set([...intersection].filter((k) => r.has(k)))
        : r;
    }
    if (!intersection || intersection.size === 0) return null;
    return [...intersection].sort(
      (a, b) => (topoIndex.get(a) ?? 0) - (topoIndex.get(b) ?? 0)
    )[0];
  };

  const visit = (startKey, stopKey, suffix) => {
    let cur = startKey;
    const stopIdx =
      stopKey != null ? topoIndex.get(stopKey) ?? Infinity : Infinity;
    while (
      cur != null &&
      cur !== stopKey &&
      (topoIndex.get(cur) ?? 0) <= stopIdx
    ) {
      if (out.has(cur)) return; // convergence already numbered upstream
      out.set(cur, `${(depth.get(cur) ?? 0) + 1}${suffix}`);
      const succs = adjacency.get(cur) || [];
      if (succs.length === 0) return;
      if (succs.length === 1) {
        cur = succs[0];
        continue;
      }
      const join = joinOf(cur);
      // Same rule the block-tree parser uses: a fork whose arms don't all meet
      // again inside THIS sequence — because one finishes the delivery, or
      // because one jumps past the enclosing join — still lives inside it, so an
      // arm that can reach our stop must stop there. Otherwise its branch suffix
      // leaks onto the trunk and a convergence prints as "6.2.2.2" not "6".
      const joinPastStop =
        join != null && stopKey != null && (topoIndex.get(join) ?? 0) > stopIdx;
      if (join == null || joinPastStop) {
        const armStop = (s) => {
          if (stopKey == null) return null;
          const r = reachableDownstream(stages, s);
          r.add(s);
          return r.has(stopKey) || joinPastStop ? stopKey : null;
        };
        succs.forEach((s, i) => visit(s, armStop(s), `${suffix}.${i + 1}`));
        if (!joinPastStop) return;
        cur = stopKey; // the block still flows on to our own stop
        continue;
      }
      succs.forEach((s, i) => visit(s, join, `${suffix}.${i + 1}`));
      cur = join; // the join returns to the ENCLOSING suffix
    }
  };

  if (entry) visit(entry.stageKey, null, "");

  // Fallback for shapes the nested walk can't address (cross-merges authored
  // via API): column number, disambiguated until unused.
  const used = new Set(out.values());
  stages.forEach((s, i) => {
    if (out.has(s.stageKey)) return;
    const col = (depth.get(s.stageKey) ?? i) + 1;
    let candidate = String(col);
    let n = 1;
    while (used.has(candidate)) {
      candidate = `${col}.${n}`;
      n += 1;
    }
    used.add(candidate);
    out.set(s.stageKey, candidate);
  });
  return out;
}

